import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ChartCard from "../../../common/components/ChartCard";
import OversightSkeleton from "../../../common/components/OversightSkeleton";
import LayoutControl from "../common/LayoutControl";
import SalariesByFiscalYearChart from "./charts/SalariesByFiscalYearChart";
import SalariesByPeriodChart from "./charts/SalariesByPeriodChart";
import SelectedDistrictSalariesByFiscalYearChart from "./charts/SelectedDistrictSalariesByFiscalYearChart";
import SelectedDistrictSalariesByPeriodChart from "./charts/SelectedDistrictSalariesByPeriodChart";
import SalariesByDistrictChart from "./charts/SalariesByDistrictChart";

const SalariesOversight = (props) => {
  const { user, loading } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    salariesByFiscalYear: [],
    salariesByPeriod: [],
    districtSalariesComparison: [],
    selectedDistrictSalaryHistory: [],
    selectedDistrictSalaryByPeriod: [],
  });
  const [layout, setLayout] = useState("default");
  const [exportLoading, setExportLoading] = useState({ image: false, pdf: false });
  const contentRef = useRef(null);

  useEffect(() => {
    if (user?.selectedEntity) {
      getSalariesData();
    }
  }, [user?.selectedEntity]);

  const getSalariesData = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`/api/oversight/salaries`);
      setData(data);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleExportImage = async () => {
    try {
      setExportLoading(prev => ({ ...prev, image: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const link = document.createElement("a");
      link.download = `Salaries-Overview-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (error) {
      toast.error("Failed to export image. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, image: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(prev => ({ ...prev, pdf: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "l" : "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = (pdfHeight - imgHeight * ratio) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
        "",
        "FAST"
      );
      pdf.save(`Salaries-Overview-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  const getLayoutClass = (index) => {
    switch (layout) {
      case "leftWide":
        return index % 2 === 0 ? "col-12 col-md-8 p-0" : "col-12 col-md-4 p-0";
      case "rightWide":
        return index % 2 === 0 ? "col-12 col-md-4 p-0" : "col-12 col-md-8 p-0";
      case "full":
        return "col-12 p-0";
      default:
        return "col-12 col-md-6 p-0";
    }
  };

  if (isLoading || loading) {
    return <OversightSkeleton />;
  }

  return (
    <div className="pl-4 pt-2">
      <LayoutControl 
        layout={layout} 
        setLayout={setLayout} 
        onExportImage={handleExportImage}
        onExportPDF={handleExportPDF}
        exportLoading={exportLoading}
      />
      <div className="row w-100" ref={contentRef}>
        {[...Array(4)].map((_, index) => (
          <div key={index} className={getLayoutClass(index)}>
            {index === 0 && user?.selectedEntity?.id === "00000000" && (
              <ChartCard
                title="Teacher Salaries by Fiscal Year"
                subheader="Distribution of teacher salaries by fiscal year"
              >
                <SalariesByFiscalYearChart data={data?.salariesByFiscalYear} />
              </ChartCard>
            )}
            {index === 1 && user?.selectedEntity?.id === "00000000" && (
              <ChartCard
                title="Teacher Salaries by Period"
                subheader="Distribution of teacher salaries by period"
              >
                <SalariesByPeriodChart data={data?.salariesByPeriod} />
              </ChartCard>
            )}
            {index === 2 && user?.selectedEntity?.id !== "00000000" && (
              <ChartCard
                title={`Teacher Salaries by Fiscal Year In ${user?.selectedEntity?.name}`}
                subheader={`Distribution of teacher salaries by fiscal year in ${user?.selectedEntity?.name}`}
              >
                <SelectedDistrictSalariesByFiscalYearChart
                  data={data.selectedDistrictSalaryHistory}
                />
              </ChartCard>
            )} 
            {index === 3 && user?.selectedEntity?.id !== "00000000" && (
              <ChartCard
                title={`Teacher Salaries by Period In ${user?.selectedEntity?.name}`}
                subheader={`Distribution of teacher salaries by period in ${user?.selectedEntity?.name}`}
              >
                <SelectedDistrictSalariesByPeriodChart
                  data={data?.selectedDistrictSalaryByPeriod}
                />
              </ChartCard>
            )}
          </div>
        ))}

        {user?.selectedEntity?.id === "00000000" && (
          <div className="col-12 p-0">
            <ChartCard
              title="Teacher Salaries by District"
              subheader="Distribution of teacher salaries by district"
            >
              <SalariesByDistrictChart
                data={data?.districtSalariesComparison}
              />
            </ChartCard>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};

export default connect(mapStateToProps, {})(SalariesOversight);
